import { Injectable, signal, computed, inject, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, throwError, timer } from 'rxjs';
import { tap, catchError, map, retry } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AuthPort, MyOrganization } from '@core/ports/auth.port';
import { User } from '@domains/auth/models/user.model';
import { LoginRequest, RegisterRequest, ValidateInvitationResponse } from '@domains/auth/models/auth-request.model';
import { ApiResponse, LoginResponseData, RegisterResponseData, RefreshResponseData, AvatarUploadResponseData } from '@domains/auth/models/auth-response.model';
import type {
  ChangePasswordRequestDto,
  RefreshRequestDto,
  SwitchOrgRequestDto,
  UpdateProfileRequestDto,
} from '@shared/types/api-types.helpers';
import { TokenService } from './token.service';
import { FeatureService } from '@core/services/feature.service';
import { NotificationService } from '@core/services/notification.service';
import { SKIP_AUTH_LOGOUT } from '@core/interceptors/auth.interceptor';

/** Estados semánticos del gate de autenticación derivados de {@link AuthService.authStatus}. */
export type AuthStatus = 'bootstrapping' | 'authenticated' | 'unauthenticated' | 'error';

/**
 * basement-cms chassis — THINNED from basement's AuthService.
 *
 * Divergence recorded for the seed audit: the supply-chain inject()s of
 * GuideFeaturesService, BrandingService, CurrencyService, DemoDataService and
 * NavigationService were removed (those domains do not exist in the CMS). The
 * `AuthPort` contract is unchanged. Post-login redirect uses `Router` directly
 * instead of NavigationService; localStorage cross-tab key renamed `sc_`→`cms_`.
 * NotificationService is kept for the inactive-org error toast.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService implements AuthPort {
  /**
   * CMS permissions implied by role. Mirrors the BFF management middleware
   * (`internal/cms/management/handler.go`: `cms:edit` / `cms:publish` are
   * granted to `admin` y `super_admin` por ROL), porque el array
   * `permissions` que retorna `GET /auth/me` NO incluye los permisos CMS
   * (`UserRole.DefaultPermissions()` no los lista). Sin este espejo, los
   * gates `*appHasPermission="'cms:edit'"` ocultaban "Nuevo tipo" y las
   * acciones por fila para el propio rol admin.
   */
  private static readonly CMS_ROLE_PERMISSIONS: Record<string, readonly string[]> = {
    admin: ['cms:edit', 'cms:publish'],
    super_admin: ['cms:edit', 'cms:publish'],
  };

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly tokenService = inject(TokenService);
  private readonly featureService = inject(FeatureService);
  private readonly notificationService = inject(NotificationService);
  private readonly ngZone = inject(NgZone);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly apiUrl = `${environment.authAPI}/auth`;

  private readonly currentUserSignal = signal<User | null>(null);
  private readonly isAuthenticatedSignal = signal<boolean>(false);
  private readonly isLoadingSignal = signal<boolean>(false);
  private readonly myOrganizationsSignal = signal<MyOrganization[]>([]);
  readonly status = signal<'loading' | 'loaded' | 'error' | 'idle'>('idle');

  /**
   * Active organization id derived from the JWT `organizationId` claim
   * (Path C — see ADR-AI-17). This is the source-of-truth for orgScoped
   * services/components, NOT `currentUser().organizationId` (no such field).
   */
  readonly currentOrganizationId = signal<string | null>(null);

  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly isAuthenticated = this.isAuthenticatedSignal.asReadonly();
  readonly isLoading = this.isLoadingSignal.asReadonly();
  readonly myOrganizations = this.myOrganizationsSignal.asReadonly();

  /**
   * Estado semántico derivado del ciclo de vida de autenticación.
   */
  readonly authStatus = computed<AuthStatus>(() => {
    const s = this.status();
    const authed = this.isAuthenticatedSignal();
    const statusMap: Record<string, AuthStatus> = {
      loading: 'bootstrapping',
      error: 'error',
    };
    if (statusMap[s]) return statusMap[s];
    if (s === 'loaded' && authed) return 'authenticated';
    return 'unauthenticated';
  });

  readonly userName = computed(() => {
    const user = this.currentUserSignal();
    return user ? `${user.firstName} ${user.lastName}` : '';
  });

  constructor() {
    this.initializeAuth();
    this.listenForCrossTabChanges();
  }

  /**
   * Bootstraps auth state from a persisted token: hydrates the active org,
   * restores the saved user, refreshes the user/features, and refreshes the
   * token when it is already expired.
   * @returns void
   */
  private initializeAuth(): void {
    const token = this.tokenService.getAccessToken();
    if (!token) return;

    if (this.tokenService.isTokenExpired(token)) {
      this.tryRefreshToken();
      return;
    }

    this.status.set('loading');
    this.isAuthenticatedSignal.set(true);

    // Hydrate active org id from the persisted JWT claim (Path C — ADR-AI-17).
    this.currentOrganizationId.set(this.tokenService.extractOrgIdFromToken(token));

    // Restore user from localStorage while fetching fresh data
    const savedUser = this.tokenService.getSavedUser() as User | null;
    if (savedUser) {
      this.currentUserSignal.set(savedUser);
      this.featureService.setSuperAdmin((savedUser as User).role === 'super_admin');
    }

    this.fetchCurrentUser().subscribe({
      next: (user) => {
        this.currentUserSignal.set(user);
        this.tokenService.saveUser(user as unknown as Record<string, unknown>);
        this.status.set('loaded');
        this.featureService.setSuperAdmin(user.role === 'super_admin');
        this.featureService.loadFeatures().subscribe();
        this.listMyOrganizations().subscribe();
      },
      error: (err: HttpErrorResponse) => {
        // Un error de `/auth/me` distinto de 401 NUNCA cierra la sesión: el BFF
        // de staging devuelve 403 espurios ("Organization is inactive") bajo
        // requests concurrentes, y expulsar al login por eso destruía sesiones
        // válidas. La sesión solo se cierra ante un 401 real con refresh
        // fallido (lo maneja el interceptor + `refreshAccessToken`). Acá se
        // degrada al usuario persistido y, si el 403 persistió tras los
        // reintentos, se avisa sin desloguear.
        if (err?.status === 403) {
          this.notificationService.warning(
            'No se pudo validar tu organización. Si el problema persiste, consultá al administrador.',
            { duration: 8000 }
          );
        }
        if (savedUser) {
          this.status.set('loaded');
          this.featureService.loadFeatures().subscribe();
          this.listMyOrganizations().subscribe();
        } else {
          this.clearSession();
          this.status.set('idle');
        }
      },
    });
  }

  /**
   * Authenticates the user, persists tokens + user, and loads org features.
   * @param email User email.
   * @param password User password.
   * @returns An observable emitting the authenticated user.
   */
  login(email: string, password: string): Observable<User> {
    this.isLoadingSignal.set(true);

    const payload: LoginRequest = { email, password };

    return this.http.post<ApiResponse<LoginResponseData>>(`${this.apiUrl}/login`, payload).pipe(
      map((response) => {
        const { user, tokens } = response.data;
        this.tokenService.setAccessToken(tokens.accessToken);
        this.tokenService.setRefreshToken(tokens.refreshToken);
        this.tokenService.saveUser(user as unknown as Record<string, unknown>);
        this.currentUserSignal.set(user);
        this.currentOrganizationId.set(this.tokenService.extractOrgIdFromToken(tokens.accessToken));
        this.isAuthenticatedSignal.set(true);
        this.isLoadingSignal.set(false);
        this.status.set('loaded');
        this.featureService.setSuperAdmin(user.role === 'super_admin');
        this.featureService.loadFeatures().subscribe();
        this.listMyOrganizations().subscribe();
        return user;
      }),
      catchError((error) => {
        this.isLoadingSignal.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Registers a new user account.
   * @param data Registration payload (matches `RegisterRequest`).
   * @returns An observable for the registration response.
   */
  register(data: Record<string, unknown>): Observable<unknown> {
    return this.http.post<ApiResponse<RegisterResponseData>>(
      `${this.apiUrl}/register`,
      data as unknown as RegisterRequest
    );
  }

  /**
   * Requests a password-reset email for an account.
   * @param data Object carrying the account `email`.
   * @returns An observable for the request response.
   */
  requestPasswordReset(data: { email: string }): Observable<unknown> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/password-reset/request`, data);
  }

  /**
   * Confirms a password reset with a token and a new password.
   * @param data Object carrying the reset `token` and `newPassword`.
   * @returns An observable for the confirmation response.
   */
  confirmPasswordReset(data: { token: string; newPassword: string }): Observable<unknown> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/password-reset/confirm`, data);
  }

  /**
   * Verifies an email address with a confirmation token.
   * @param data Object carrying the verification `token`.
   * @returns An observable for the verification response.
   */
  verifyEmail(data: { token: string }): Observable<unknown> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/verify-email`, data);
  }

  /**
   * Resends the verification email to an address.
   * @param email Email address to resend verification to.
   * @returns An observable for the resend response.
   */
  resendVerificationEmail(email: string): Observable<unknown> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/resend-verification`, { email });
  }

  /**
   * Validates an invitation token before registration.
   * @param token Invitation token.
   * @returns An observable emitting the invitation validation result.
   */
  validateInvitation(token: string): Observable<ValidateInvitationResponse> {
    return this.http.get<ApiResponse<ValidateInvitationResponse>>(
      `${this.apiUrl}/invitations/${token}`
    ).pipe(map((response) => response.data));
  }

  /**
   * Uploads a new avatar image and updates the current user.
   * @param file Avatar image file.
   * @returns An observable emitting the upload result (with the new avatar URL).
   */
  uploadAvatar(file: File): Observable<AvatarUploadResponseData> {
    const formData = new FormData();
    formData.append('avatar', file);

    return this.http.post<ApiResponse<AvatarUploadResponseData>>(
      `${environment.authAPI}/upload/avatar`,
      formData
    ).pipe(
      map((response) => {
        this.updateUserAvatar(response.data.avatarUrl);
        return response.data;
      })
    );
  }

  /**
   * Deletes the current user's avatar.
   * @returns An observable for the delete response.
   */
  deleteAvatar(): Observable<unknown> {
    return this.http.delete<ApiResponse>(`${environment.authAPI}/upload/avatar`).pipe(
      tap(() => this.updateUserAvatar(undefined))
    );
  }

  /**
   * Changes the current user's password.
   * @param currentPassword Current password.
   * @param newPassword New password.
   * @returns An observable that completes on success.
   */
  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    const body: ChangePasswordRequestDto = { currentPassword, newPassword };
    return this.http.put<ApiResponse>(`${this.apiUrl}/change-password`, body).pipe(
      map(() => undefined)
    );
  }

  /**
   * Updates the current user's profile and refreshes the cached user.
   * @param data Profile update payload (matches `UpdateProfileRequestDto`).
   * @returns An observable emitting the updated user.
   */
  updateProfile(data: Record<string, unknown>): Observable<User> {
    return this.http.put<ApiResponse<User>>(`${this.apiUrl}/me`, data as unknown as UpdateProfileRequestDto).pipe(
      map((response) => {
        const user = response.data;
        this.currentUserSignal.set(user);
        this.tokenService.saveUser(user as unknown as Record<string, unknown>);
        return user;
      })
    );
  }

  /**
   * Updates the avatar on the cached current user (and persisted copy).
   * @param avatarUrl New avatar URL, or `undefined` to clear it.
   * @returns void
   */
  private updateUserAvatar(avatarUrl: string | undefined): void {
    const current = this.currentUserSignal();
    if (!current) return;
    const updated = { ...current, avatar: avatarUrl };
    this.currentUserSignal.set(updated);
    this.tokenService.saveUser(updated as unknown as Record<string, unknown>);
  }

  /**
   * Lists the organizations the current user belongs to and caches them.
   * @returns An observable emitting the user's organizations.
   */
  listMyOrganizations(): Observable<MyOrganization[]> {
    return this.http.get<ApiResponse<MyOrganization[]>>(`${this.apiUrl}/me/organizations`).pipe(
      map((response) => {
        const orgs = response.data ?? [];
        this.myOrganizationsSignal.set(orgs);
        return orgs;
      })
    );
  }

  /**
   * Cambia la organización activa del usuario. Persiste tokens + user, refresca
   * features y emite el cambio de `currentOrganizationId`. Navega a `/admin`
   * con `Router.navigate` (SPA, sin reload). Ver ADR-AI-17.
   * @param organizationId Id de la organización destino.
   * @returns Observable que emite los datos de sesión re-scopeados.
   */
  switchOrg(organizationId: string): Observable<unknown> {
    const body: SwitchOrgRequestDto = { organizationId };
    return this.http.post<ApiResponse<LoginResponseData>>(`${this.apiUrl}/switch-org`, body, {
      context: new HttpContext().set(SKIP_AUTH_LOGOUT, true),
    }).pipe(
      map((response) => {
        const { user, tokens } = response.data;
        this.tokenService.setAccessToken(tokens.accessToken);
        this.tokenService.setRefreshToken(tokens.refreshToken);
        this.tokenService.saveUser(user as unknown as Record<string, unknown>);
        this.currentUserSignal.set(user);
        this.currentOrganizationId.set(this.tokenService.extractOrgIdFromToken(tokens.accessToken));
        this.featureService.setSuperAdmin(user.role === 'super_admin');
        this.featureService.loadFeatures().subscribe();
        this.router.navigate(['/admin']);
        return response.data;
      })
    );
  }

  /**
   * Refreshes the access token (exponential backoff + jitter, up to 3 retries).
   * On a 401/403 it clears the session and redirects to login.
   * @returns An observable emitting the new access token.
   */
  refreshAccessToken(): Observable<string> {
    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }

    const body: RefreshRequestDto = { refreshToken };
    return this.http.post<ApiResponse<RefreshResponseData>>(`${this.apiUrl}/refresh`, body).pipe(
      retry({
        count: 3,
        delay: (error: HttpErrorResponse, retryCount) => {
          if (error.status === 401 || error.status === 403) {
            return throwError(() => error);
          }
          const base = 500 * Math.pow(3, retryCount - 1); // 500, 1500, 4500
          const jitter = base * (Math.random() * 0.4 - 0.2); // ±20%
          return timer(base + jitter);
        },
      }),
      map((response) => {
        const { accessToken } = response.data;
        this.tokenService.setAccessToken(accessToken);
        this.currentOrganizationId.set(this.tokenService.extractOrgIdFromToken(accessToken));
        return accessToken;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          this.clearSession();
          // replaceUrl: la página privada muerta no queda en el historial
          // ("atrás" nunca aterriza en un login no visitado — ADR-008 §5).
          this.router.navigate(['/auth/login'], { replaceUrl: true });
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Logs out: best-effort server logout, closes dialogs, clears the session and
   * navigates to the login page.
   * @returns void
   */
  logout(): void {
    const refreshToken = this.tokenService.getRefreshToken();
    if (refreshToken) {
      const body: RefreshRequestDto = { refreshToken };
      this.http.post(`${this.apiUrl}/logout`, body).subscribe();
    }
    this.dialog.closeAll();
    this.clearSession();
    // replaceUrl: tras el logout, "atrás" no vuelve a la página privada muerta.
    this.router.navigate(['/auth/login'], { replaceUrl: true });
  }

  /**
   * Clears the local session (tokens + signals) without API call or navigation.
   * @returns void
   */
  silentLogout(): void {
    this.clearSession();
  }

  /**
   * Effective permission set for the current user: the explicit array from
   * `GET /auth/me` plus the CMS permissions implied by role (see
   * {@link AuthService.CMS_ROLE_PERMISSIONS}). Reads `currentUserSignal` so
   * reactive consumers (e.g. `HasPermissionDirective`) re-evaluate on changes.
   * @returns The effective permissions, empty when no session.
   */
  private effectivePermissions(): readonly string[] {
    const user = this.currentUserSignal();
    if (!user) return [];
    const implied = AuthService.CMS_ROLE_PERMISSIONS[user.role] ?? [];
    return [...(user.permissions ?? []), ...implied];
  }

  /**
   * Tells whether the current user holds a specific permission
   * (explicit or implied by role).
   * @param permission Permission key to check.
   * @returns `true` when the user holds the permission.
   */
  hasPermission(permission: string): boolean {
    return this.effectivePermissions().includes(permission);
  }

  /**
   * Tells whether the current user holds at least one of the given permissions.
   * @param permissions Permission keys to check.
   * @returns `true` when the user holds any of them.
   */
  containsAny(permissions: string[]): boolean {
    const userPermissions = this.effectivePermissions();
    if (userPermissions.length === 0) return false;
    return permissions.some((p) => userPermissions.includes(p));
  }

  /**
   * Tells whether the current user holds all of the given permissions.
   * @param permissions Permission keys to check.
   * @returns `true` when the user holds every one of them.
   */
  containsAll(permissions: string[]): boolean {
    const userPermissions = this.effectivePermissions();
    if (userPermissions.length === 0) return false;
    return permissions.every((p) => userPermissions.includes(p));
  }

  /**
   * Tells whether the current user has a specific role.
   * @param role Role identifier to check.
   * @returns `true` when the user's role matches.
   */
  hasRole(role: string): boolean {
    return this.currentUserSignal()?.role === role;
  }

  /**
   * Fetches the current user from the BFF (skipping the auth-logout interceptor branch).
   *
   * Reintenta con backoff (400ms, 1200ms) los fallos transitorios: red/0, 5xx
   * y TAMBIÉN 403 — el BFF de staging responde 403 espurio ("Organization is
   * inactive") en `/auth/me` bajo requests concurrentes (en serie responde
   * 200). El mismo workaround documentado que `retryTransient` en el SDK; un
   * 401 (token vencido) NO se reintenta: lo resuelve el interceptor vía refresh.
   * @returns An observable emitting the current user.
   */
  private fetchCurrentUser(): Observable<User> {
    return this.http.get<ApiResponse<User>>(`${this.apiUrl}/me`, {
      context: new HttpContext().set(SKIP_AUTH_LOGOUT, true),
    }).pipe(
      retry({
        count: 2,
        delay: (error: HttpErrorResponse, retryCount) => {
          const transient = error.status === 0 || error.status >= 500 || error.status === 403;
          if (!transient) {
            return throwError(() => error);
          }
          return timer(400 * Math.pow(3, retryCount - 1)); // 400ms, 1200ms
        },
      }),
      map((response) => response.data)
    );
  }

  /**
   * Attempts a token refresh during bootstrap; clears the session on failure.
   * @returns void
   */
  private tryRefreshToken(): void {
    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) {
      this.clearSession();
      return;
    }

    this.status.set('loading');
    this.refreshAccessToken().subscribe({
      next: () => this.initializeAuth(),
      error: () => {
        this.clearSession();
        this.status.set('idle');
      },
    });
  }

  /**
   * Listens for localStorage changes from other tabs to sync auth state
   * (re-bootstraps on a new token, clears + redirects when the token is removed).
   * No-op under SSR (S7 Fase B): there is no `window` on the server.
   * @returns void
   */
  private listenForCrossTabChanges(): void {
    if (!this.isBrowser) return;
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== 'cms_access_token') return;

      this.ngZone.run(() => {
        if (event.newValue) {
          this.initializeAuth();
        } else {
          this.clearSession();
          this.status.set('idle');
          // Logout cross-tab: expulsión con replaceUrl (ADR-008 §5).
          this.router.navigate(['/auth/login'], { replaceUrl: true });
        }
      });
    });
  }

  /**
   * Clears all local session state (tokens, user, org, organizations, features).
   * @returns void
   */
  private clearSession(): void {
    this.tokenService.clearTokens();
    this.currentUserSignal.set(null);
    this.currentOrganizationId.set(null);
    this.isAuthenticatedSignal.set(false);
    this.myOrganizationsSignal.set([]);
    this.featureService.clear();
  }
}
